"use client";

import clsx from "clsx";

export interface Position {
  id: string;
  asset: string;
  side: "long" | "short";
  size: number;
  entryPrice: number;
  leverage: number;
  margin: number;
  liquidationPrice: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  takeProfitPrice: number | null;
  stopLossPrice: number | null;
  status: "open" | "closed";
}

interface PositionCardProps {
  position: Position;
  currentPrice: number;
  onClose?: (positionId: string) => void;
  onEdit?: (positionId: string) => void;
}

export const PositionCard = ({
  position,
  currentPrice,
  onClose,
  onEdit,
}: PositionCardProps) => {
  const isLong = position.side === "long";
  const isProfitable = position.unrealizedPnl >= 0;

  // Calculate distance to liquidation
  const distanceToLiquidation = isLong
    ? ((currentPrice - position.liquidationPrice) / currentPrice) * 100
    : ((position.liquidationPrice - currentPrice) / currentPrice) * 100;

  const isAtRisk = distanceToLiquidation < 10;

  return (
    <div
      className={clsx(
        "rounded-xl border p-4 transition",
        isAtRisk ? "border-rose-500/50 bg-rose-500/10" : "border-slate-200/60 bg-white/5"
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">{position.asset}</span>
          <span
            className={clsx(
              "rounded-full px-2 py-0.5 text-xs font-semibold",
              isLong
                ? "bg-emerald-500/20 text-emerald-400"
                : "bg-rose-500/20 text-rose-400"
            )}
          >
            {position.side.toUpperCase()} {position.leverage}x
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit?.(position.id)}
            className="rounded px-2 py-1 text-xs text-slate-400 transition hover:bg-slate-700 hover:text-white"
          >
            TP/SL
          </button>
          <button
            onClick={() => onClose?.(position.id)}
            className="rounded bg-slate-700 px-2 py-1 text-xs text-white transition hover:bg-slate-600"
          >
            Close
          </button>
        </div>
      </div>

      {/* Position Details Grid */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-slate-500">Size</p>
          <p className="text-white">${position.size.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-slate-500">Entry Price</p>
          <p className="text-white">${position.entryPrice.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-slate-500">Mark Price</p>
          <p className="text-white">${currentPrice.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-slate-500">Margin</p>
          <p className="text-white">${position.margin.toLocaleString()}</p>
        </div>
      </div>

      {/* PnL Section */}
      <div className="mt-3 border-t border-slate-700 pt-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Unrealized PnL</span>
          <span
            className={clsx(
              "text-sm font-semibold",
              isProfitable ? "text-emerald-400" : "text-rose-400"
            )}
          >
            {isProfitable ? "+" : ""}${position.unrealizedPnl.toFixed(2)}
            <span className="ml-1 text-xs">
              ({isProfitable ? "+" : ""}
              {position.unrealizedPnlPercent.toFixed(2)}%)
            </span>
          </span>
        </div>
      </div>

      {/* Liquidation Warning */}
      <div
        className={clsx(
          "mt-3 rounded-lg px-3 py-2 text-xs",
          isAtRisk ? "bg-rose-500/20" : "bg-slate-800/50"
        )}
      >
        <div className="flex items-center justify-between">
          <span className={isAtRisk ? "text-rose-400" : "text-slate-500"}>
            Liq. Price
          </span>
          <span
            className={clsx("font-medium", isAtRisk ? "text-rose-400" : "text-slate-300")}
          >
            ${position.liquidationPrice.toFixed(2)}
          </span>
        </div>
        {isAtRisk && (
          <p className="mt-1 text-rose-400">
            ⚠️ Position at risk ({distanceToLiquidation.toFixed(1)}% from liquidation)
          </p>
        )}
      </div>

      {/* TP/SL Display */}
      {(position.takeProfitPrice || position.stopLossPrice) && (
        <div className="mt-2 flex gap-4 text-xs">
          {position.takeProfitPrice && (
            <div>
              <span className="text-slate-500">TP: </span>
              <span className="text-emerald-400">
                ${position.takeProfitPrice.toFixed(2)}
              </span>
            </div>
          )}
          {position.stopLossPrice && (
            <div>
              <span className="text-slate-500">SL: </span>
              <span className="text-rose-400">${position.stopLossPrice.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
